import React from 'react';
import { ExtraTripCategory } from '@/constants/ExtraTripsConstants';
import { trainingColumns } from '@/constants/tableColumns/TrainingPlanningTableColumns';
import { Team } from '../../types/ViewSchedule';
import { cn } from '../../utils/cn';
import { getColorByTeamName } from '../../utils/getColorByTeamName';
import { getSubtitleByTeamName } from '../../utils/getSubtitleByTeamName';
import Table from '../organisms/Table';
import NewTeamIndicator from './NewTeamIndicator';
import {
	Tooltip,
	TooltipArrow,
	TooltipContent,
	TooltipPortal,
	TooltipProvider,
	TooltipTrigger,
} from './ToolTip';

interface ScheduleTeamProps {
	scheduleTeamName: Team;
	isFirstOfGroup: boolean;
	isLastOfGroup: boolean;
	borderRed?: boolean | undefined;
	message?: string;
	newTeam: string | undefined;
	workShiftExtensionMessage?: string | undefined;
	onNoticeActivation: boolean | undefined;
	onNoticeActivationMessage: string | undefined;
	trainingTimes?: Array<{
		startTime: string;
		endTime: string;
		name: string;
		date: string;
		location: string;
	}>;
	extraTripDetails?: {
		category: string;
		teamName: string;
		reason?: string;
		justified?: string;
		detailing?: string;
		coveredVacancy?: string;
		coveredEmployeeName?: string;
	};
	coveredByExtraTripEmployeeName?: string;
}

const getExtraTripCategorySubtitle = (category: ExtraTripCategory) => {
	switch (category) {
		case ExtraTripCategory.VacancyCovering:
			return 'Cobertura de vaga';
		case ExtraTripCategory.Absenteeism:
			return 'Absenteísmo';
		case ExtraTripCategory.SpecialServices:
			return 'Serviços Especiais';
	}
};

const ScheduleTeam: React.FC<ScheduleTeamProps> = React.memo(
	({
		scheduleTeamName,
		isFirstOfGroup,
		isLastOfGroup,
		borderRed,
		message,
		newTeam,
		workShiftExtensionMessage,
		onNoticeActivation,
		onNoticeActivationMessage,
		trainingTimes,
		extraTripDetails,
		coveredByExtraTripEmployeeName,
	}) => {
		const extraTripTeamNames = ['IE', 'IE !', 'IED', 'IED !', 'IEN', 'IEN !'];
		const isExtraTrip = extraTripTeamNames.includes(scheduleTeamName);
		const isVacancyCoveringOrAbsenteeism = [
			ExtraTripCategory.VacancyCovering,
			ExtraTripCategory.Absenteeism,
		].includes(extraTripDetails?.category as ExtraTripCategory);

		const dailyTrainings =
			trainingTimes?.map((training) => ({
				startHour: training.startTime,
				name: training.name,
				location: training.location,
				endHour: training.endTime,
				date: training.date,
			})) ?? [];
		return (
			<div
				className={cn(
					'relative z-10 flex h-6 cursor-pointer select-none items-center justify-center text-sm font-normal',
					getColorByTeamName(scheduleTeamName),
					borderRed && 'border-2 border-red',
					isFirstOfGroup && 'ml-[2px] rounded-l-sm',
					isLastOfGroup && 'mr-[2px] rounded-r-sm',
					scheduleTeamName === 'N/A' && 'bg-transparent text-gray-400',
				)}
			>
				<TooltipProvider delayDuration={500} skipDelayDuration={200}>
					<Tooltip>
						<TooltipTrigger asChild>
							<div
								className={cn(
									'flex w-full justify-center text-white',
									(scheduleTeamName === 'N/A' || scheduleTeamName === '-') &&
										'bg-transparent text-gray-400',
								)}
							>
								{coveredByExtraTripEmployeeName && '-'}
								{scheduleTeamName}
								{workShiftExtensionMessage !== undefined &&
									!onNoticeActivation &&
									'*'}
								{message?.includes('sobreaviso') && (
									<div className="on-notice-div">
										SA{onNoticeActivation && '*'}
									</div>
								)}
							</div>
						</TooltipTrigger>
						<TooltipPortal>
							<TooltipContent
								className=" bg-white"
								align="center"
								sideOffset={1}
								arrowPadding={50}
								side="left"
							>
								<TooltipArrow className="fill-white" />
								{scheduleTeamName === 'TR' ? (
									<div className="min-w-[30rem] ">
										<span className="text-lg font-semibold text-[#193CB9]">
											Treinamentos do dia
										</span>
										<div className="custom-scrollbar max-h-[50vh] overflow-y-auto">
											<Table
												columns={trainingColumns}
												data={dailyTrainings}
												containerClassname="text-center w-full"
												trBodyClassname="even:bg-white"
											/>
										</div>
									</div>
								) : isExtraTrip ? (
									<div className="min-w-[30rem] ">
										<span className="text-md font-semibold text-[#193CB9]">
											Detalhes da ida extra
										</span>
										{extraTripDetails && isVacancyCoveringOrAbsenteeism ? (
											<div>
												<p>
													<span className="font-semibold">
														Tipo de Cobertura:
													</span>{' '}
													{getExtraTripCategorySubtitle(
														extraTripDetails?.category as ExtraTripCategory,
													)}
												</p>
												{extraTripDetails.category ===
													ExtraTripCategory.VacancyCovering && (
													<p>
														<span className="font-semibold">Vaga:</span>{' '}
														{extraTripDetails?.coveredVacancy}
													</p>
												)}
												{extraTripDetails.category ===
													ExtraTripCategory.Absenteeism && (
													<>
														<div className="flex gap-8 items-center">
															<p>
																<span className="font-semibold">
																	Justificada:
																</span>{' '}
																{extraTripDetails?.justified ? 'SIM' : 'NÃO'}
															</p>
															<p>
																<span className="font-semibold">
																	Justificativa:
																</span>{' '}
																{extraTripDetails?.detailing}
															</p>
														</div>
														<p>
															<span className="font-semibold">
																Colaborador coberto:
															</span>{' '}
															{extraTripDetails?.coveredEmployeeName}
														</p>
													</>
												)}
											</div>
										) : (
											<div>
												<p>
													<span className="font-semibold">Motivo:</span>{' '}
													{getExtraTripCategorySubtitle(
														extraTripDetails?.category as ExtraTripCategory,
													)}
												</p>
												<p>
													<span className="font-semibold">Detalhamento:</span>{' '}
													{extraTripDetails?.detailing}
												</p>
											</div>
										)}
									</div>
								) : (
									<>
										<p>{getSubtitleByTeamName(scheduleTeamName, message)}</p>
										{coveredByExtraTripEmployeeName && (
											<p>
												<span className="font-semibold">
													Colaborador realizando a cobertura:
												</span>{' '}
												{coveredByExtraTripEmployeeName}
											</p>
										)}
										{workShiftExtensionMessage !== undefined && (
											<p>{workShiftExtensionMessage}</p>
										)}
										{onNoticeActivation && <p>{onNoticeActivationMessage}</p>}
									</>
								)}
							</TooltipContent>
						</TooltipPortal>
					</Tooltip>
				</TooltipProvider>
				{newTeam && <NewTeamIndicator newTeam={newTeam} />}
			</div>
		);
	},
);
ScheduleTeam.displayName = 'ScheduleTeam';

export default ScheduleTeam;
